import React, { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import PasswordInput from "../UILibrary/PasswordInput";
import Submission from "../UILibrary/Submission";
import PasswordChange from "Utils/Assets/SVGs/PasswordChange";
import GoBackIcon from "Utils/Assets/SVGs/GoBackIcon";
import {
  defaultChangePasswordFormValues,
  passwordRules,
} from "Utils/Global/globalConstants";
import { validatePasswordRegExpressions } from "Utils/Global/globalfuncion";
import { IChangePasswordFormData } from "@model/Authentication.model";
import styles from "./Forms.module.css";
import { AuthenticationService } from "../../../Logic/Authentication/AuthenticationService";
import { UserAuthenticationStoreImplementation } from "Data/UserAuthentication/UserAuthenticationStoreImplementation";
import { useNavigate } from "react-router-dom";
import { useForceLogoutAndRedirect } from "../../../Logic/Functions/Hooks/useForceLogoutAndRedirect";

const ChangePasswordForm = () => {
  const { control, handleSubmit, reset } = useForm<IChangePasswordFormData>({
    mode: "onSubmit",
    defaultValues: defaultChangePasswordFormValues,
  });
  const onAuthError = useForceLogoutAndRedirect();

  const [apiError, setApiError] = useState<string | null>(null);
  const [apiMessage, setApiMessage] = useState<string | null>(null);
  const auth = new AuthenticationService();
  const [loading, setLoading] = useState(false);
  const userAuthenticationStore = UserAuthenticationStoreImplementation();
  const navigate = useNavigate();
  const onValidPassChange = async (form: IChangePasswordFormData) => {
    await auth.handleChangePassword({
      data: form,
      handleUserData: userAuthenticationStore.logUserOut,
      token: userAuthenticationStore.authenticationState?.token ?? "",
      reset,
      onAuthError,
      setApiError: (m) => setApiError(m),
      setLoading,
    });
  };

  return (
    <div className={styles.pageWrapper}>
      <div className={styles.formContainerr}>
        <form className={styles.formInner} data-testid="change_password_form">
          <div
            className={styles.verificationIconWrapper}
            data-testid="change_password_icon"
          >
            <PasswordChange />
          </div>

          <div className="margin-top--20">
            <h2
              className={styles.formTitle}
              data-testid="change_password_title"
            >
              Change Password
            </h2>

            <div className="margin-top--10">
              <p className={styles.formText} data-testid="change_password_text">
                Please enter your current password, then set a new one.
              </p>
            </div>
          </div>

          <div className={styles.cpLayout}>
            <section className={styles.cpSectionCard}>
              <div className={styles.cpSectionHeader}>
                <div>
                  <h3 className={styles.cpSectionTitle}>
                    Verify current password
                  </h3>
                  <p className={styles.cpSectionHint}>
                    For security, confirm it’s really you before changing
                    anything.
                  </p>
                </div>
              </div>

              <Controller
                name="oldPassword"
                control={control}
                rules={{ required: "Old password is required" }}
                render={({ field, fieldState }) => (
                  <PasswordInput
                    label="Old Password"
                    placeholder="Enter your current password"
                    value={field.value}
                    onChange={field.onChange}
                    error={fieldState.error?.message}
                    rules={[]}
                  />
                )}
              />
            </section>

            <section className={styles.cpSectionCard}>
              <div className={styles.cpSectionHeader}>
                <div>
                  <h3 className={styles.cpSectionTitle}>Set a new password</h3>
                  <p className={styles.cpSectionHint}>
                    Choose a strong password and confirm it below.
                  </p>
                </div>
              </div>

              <Controller
                name="newPassword"
                control={control}
                rules={{
                  required: "Password is required",
                  validate: validatePasswordRegExpressions,
                }}
                render={({ field, fieldState }) => (
                  <PasswordInput
                    label="New Password"
                    placeholder="Enter your new password"
                    value={field.value}
                    onChange={field.onChange}
                    error={fieldState.error?.message}
                    rules={passwordRules}
                  />
                )}
              />

              <Controller
                name="confirmPassword"
                control={control}
                rules={{
                  required: "Password Confirmation is required",
                  validate: (value) =>
                    value === control._formValues.newPassword ||
                    "Passwords do not match",
                }}
                render={({ field, fieldState }) => (
                  <PasswordInput
                    label="Confirm New Password"
                    placeholder="Re-enter your new password"
                    value={field.value}
                    onChange={field.onChange}
                    error={fieldState.error?.message}
                    rules={[]}
                  />
                )}
              />
            </section>
          </div>

          {apiMessage && (
            <div
              className={styles.successMessage}
              data-testid="change_password_success"
            >
              <p>{apiMessage}</p>
            </div>
          )}

          <div className="margin-top-15">
            <Submission
              buttonText="Change Password"
              onClick={handleSubmit(onValidPassChange)}
              error={apiError || undefined}
            />

            <button
              className={styles.goBackButton}
              type="button"
              onClick={() => {
                if (window.history.length > 1) navigate(-1);
                else navigate("/");
              }}
              data-testid="go_back_button"
            >
              <GoBackIcon className={styles.goBackIcon} />
              Go Back
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChangePasswordForm;
